import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { getCandidateTagHistory } from 'app/services/CandidateServices/CandidateServices';
import { httpSuccess } from 'app/services/serviceHelpers';
import { ICandidateTagHistory } from 'app/models/Candidate/Tags';
import { buildTagChangeHistoryRows } from '@AMIEWEB/Candidate/CandidateProfile/CandidateHeader/TagsWrapper/tagTypeHelpers';
import { CandidateHistoryModalChangeHistoryTab } from './CandidateHistoryModalChangeHistoryTab';

const HISTORY_DIALOG_ATTR = 'data-candidate-history-dialog';

const useStyles = makeStyles()(theme => ({
  paperMd: {
    width: '100%',
    minWidth: '720px',
    outline: 'none',
  },
  tabs: {
    borderBottom: '1px solid #e0e0e0',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '& .MuiTab-root': {
      textTransform: 'none',
      minHeight: 44,
    },
  },
  tabPanel: {
    minHeight: 320,
    paddingTop: theme.spacing(1),
  },
  tableWrap: {
    fontSize: 12,
  },
  tableContainer: {
    padding: '24px',
  },
  tableHead: {
    backgroundColor: '#fff',
    fontWeight: 'bold',
  },
  tableCell: {
    fontSize: 14,
    verticalAlign: 'top',
  },
  tableHeaderCell: {
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: '15px',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '48px 24px',
  },
  emptyState: {
    padding: '24px',
    color: '#707070',
  },
}));

type CandidateHistoryModalProps = {
  open: boolean;
  onClose: () => void;
  candidateId?: number;
  brandId?: number;
};

type TagHistoryTabProps = {
  candidateId: number;
  brandId: number;
  active: boolean;
};

const TagHistoryTab = ({ candidateId, brandId, active }: TagHistoryTabProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [historyRows, setHistoryRows] = useState<ICandidateTagHistory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active || !candidateId || !brandId) {
      return;
    }

    const loadTagHistory = async () => {
      setLoading(true);
      try {
        const response = await getCandidateTagHistory(candidateId, brandId);
        if (httpSuccess(response?.status) && Array.isArray(response?.data)) {
          setHistoryRows(response.data);
        } else {
          setHistoryRows([]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadTagHistory();
  }, [active, candidateId, brandId]);

  const historyColumns = useMemo(
    () => [
      { title: 'changedDate' as const, value: t('candidate.candidateHeader.tagHistoryChangedDate') },
      { title: 'changedBy' as const, value: t('candidate.candidateHeader.tagHistoryChangedBy') },
      { title: 'oldValue' as const, value: t('candidate.candidateHeader.tagHistoryOldValue') },
      { title: 'newValue' as const, value: t('candidate.candidateHeader.tagHistoryNewValue') },
    ],
    [t],
  );

  const tableRows = useMemo(
    () =>
      buildTagChangeHistoryRows(historyRows).map(row => ({
        changedDate: row.changedDate ? moment(row.changedDate).format('MM/DD/YYYY hh:mm A') : '--',
        changedBy: row.changedBy ?? '--',
        oldValue: row.oldValue ?? '--',
        newValue: row.newValue ?? '--',
      })),
    [historyRows],
  );

  if (loading) {
    return (
      <div className={classes.loader}>
        <CircularProgress size={32} />
      </div>
    );
  }

  if (tableRows.length === 0) {
    return <Typography className={classes.emptyState}>{t('candidate.candidateHeader.tagHistoryEmpty')}</Typography>;
  }

  return (
    <TableContainer elevation={0} component={Paper} className={classes.tableWrap}>
      <Table className={classes.tableContainer}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            {historyColumns.map(header => (
              <TableCell key={header.title} className={classes.tableHeaderCell}>
                {header.value}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map((row, index) => (
            <TableRow key={`${row.changedDate}-${index}`}>
              {historyColumns.map(header => (
                <TableCell key={header.title} className={classes.tableCell}>
                  {row[header.title]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const CandidateHistoryModal = ({ open, onClose, candidateId, brandId }: CandidateHistoryModalProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = useState<'changeHistory' | 'tagsHistory'>('changeHistory');

  useEffect(() => {
    if (open) {
      document.body.setAttribute(HISTORY_DIALOG_ATTR, 'true');
      document.body.setAttribute('data-dialog', 'true');
      return () => {
        document.body.removeAttribute(HISTORY_DIALOG_ATTR);
        document.body.removeAttribute('data-dialog');
      };
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setActiveTab('changeHistory');
    }
  }, [open]);

  if (!candidateId || !brandId) {
    return null;
  }

  return (
    <GenericDialog
      open={open}
      variant="blue"
      classes={{ paper: classes.paperMd }}
      dialogTitleProps={{
        text: t('tagsManagement.candidateTags.modalTitle'),
        closeButton: true,
        expandable: true,
      }}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
    >
      <Box className={classes.tabs}>
        <Tabs
          value={activeTab}
          onChange={(_, value: 'changeHistory' | 'tagsHistory') => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t('tagsManagement.candidateTags.tabs.changeHistory')} value="changeHistory" />
          <Tab label={t('tagsManagement.candidateTags.tabs.tagsHistory')} value="tagsHistory" />
        </Tabs>
      </Box>
      <Box className={classes.tabPanel}>
        {activeTab === 'changeHistory' && (
          <CandidateHistoryModalChangeHistoryTab candidateId={candidateId} active={open} />
        )}
        {activeTab === 'tagsHistory' && (
          <TagHistoryTab candidateId={candidateId} brandId={brandId} active={open} />
        )}
      </Box>
    </GenericDialog>
  );
};
